import { jsPDF } from 'jspdf';
import type { SuperStats } from '@/hooks/useSuperStats';
import { ZIFEK_SWATCHES } from '@/lib/presentationExport';

// ─────────────────────────────────────────────────────────────
// Rapport investisseurs — PDF mis en page (KPI, traction, trafic,
// contenus, géographie, classements). Généré côté navigateur.
// ─────────────────────────────────────────────────────────────

export interface ReportMeta {
  scopeLabel: string;
  startDate: string;
  endDate: string;
}

const INK: [number, number, number] = [28, 25, 23];
const ACCENT: [number, number, number] = [194, 65, 12];
const SECONDARY: [number, number, number] = [87, 83, 78];
const MUTED: [number, number, number] = [138, 130, 124];
const LINE: [number, number, number] = [232, 226, 220];
const SURFACE: [number, number, number] = [246, 243, 240];

const MARGIN = 16;

const ZIFEK_TERRACOTTA: [number, number, number] = [179, 96, 35];
const ZIFEK_NAVY: [number, number, number] = [23, 44, 67];

/** Convertit un code hex (#RRGGBB) en triplet RGB pour jsPDF. */
function hexToRgbTuple(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  return [
    parseInt(clean.substring(0, 2), 16),
    parseInt(clean.substring(2, 4), 16),
    parseInt(clean.substring(4, 6), 16),
  ];
}

function num(value: number): string {
  return Math.round(value).toLocaleString('fr-FR');
}

function pct(value: number | null): string {
  if (value == null) return '—';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
}

function formatDateFr(value: string): string {
  if (!value) return '—';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}

export function generateInvestorReport(stats: SuperStats, meta: ReportMeta): void {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - MARGIN * 2;
  let y = 0;

  const ensureSpace = (h: number) => {
    if (y + h > pageH - 18) {
      doc.addPage();
      y = MARGIN + 4;
    }
  };

  const sectionTitle = (label: string) => {
    ensureSpace(16);
    doc.setFillColor(...ACCENT);
    doc.rect(MARGIN, y - 3.5, 3, 6, 'F');
    doc.setTextColor(...INK);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(label, MARGIN + 6, y + 1.5);
    y += 10;
  };

  // ── Couverture ────────────────────────────────────────────
  doc.setFillColor(...ZIFEK_NAVY);
  doc.rect(0, 0, pageW, 80, 'F');
  doc.setFillColor(...ACCENT);
  doc.rect(0, 80, pageW, 2.5, 'F');

  // Logo Zifek (marque dessinée : pastille « Z » + wordmark)
  const markX = MARGIN;
  const markY = 14;
  const markSize = 15;
  doc.setFillColor(...ZIFEK_TERRACOTTA);
  doc.roundedRect(markX, markY, markSize, markSize, 3, 3, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(19);
  doc.text('Z', markX + markSize / 2, markY + markSize / 2 + 6.5, { align: 'center' });
  doc.setFontSize(26);
  doc.text('ZIFEK', markX + markSize + 5, markY + markSize / 2 + 4);

  doc.setFontSize(15);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(255, 255, 255);
  doc.text('Rapport investisseurs — KPIs, traction et audience', MARGIN, 42);

  doc.setFontSize(10);
  doc.setTextColor(230, 225, 220);
  doc.text(`Périmètre : ${meta.scopeLabel}`, MARGIN, 51);
  doc.text(
    `Période analysée : ${formatDateFr(meta.startDate)}  -  ${formatDateFr(meta.endDate)}`,
    MARGIN,
    57,
  );
  doc.setFontSize(9);
  doc.text(`Généré le ${new Date().toLocaleString('fr-FR')}`, MARGIN, 63);

  // Charte de couleurs Zifek
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(159, 196, 224);
  doc.text('CHARTE GRAPHIQUE ZIFEK', MARGIN, 70);
  const swatchGap = (pageW - MARGIN * 2) / ZIFEK_SWATCHES.length;
  ZIFEK_SWATCHES.forEach((swatch, i) => {
    const sx = MARGIN + i * swatchGap;
    const [r, g, b] = hexToRgbTuple(swatch.value);
    doc.setFillColor(r, g, b);
    doc.roundedRect(sx, 72.5, swatchGap - 3, 4.5, 1.2, 1.2, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(210, 214, 218);
    doc.text(swatch.label, sx + (swatchGap - 3) / 2, 79.6, { align: 'center' });
  });

  y = 92;

  // ── Synthèse exécutive (KPI) ──────────────────────────────
  sectionTitle('Synthèse exécutive');
  const kpis: { label: string; value: string; sub?: string }[] = [
    { label: 'Visites depuis la création', value: num(stats.visitsAllTime) },
    { label: 'Visiteurs uniques (total)', value: num(stats.uniqueAllTime) },
    { label: 'Visites sur la période', value: num(stats.visitsPeriod), sub: pct(stats.deltas.visits) },
    { label: 'Visiteurs uniques (période)', value: num(stats.uniquePeriod), sub: pct(stats.deltas.unique) },
    { label: 'Commandes (période)', value: num(stats.orders), sub: pct(stats.deltas.orders) },
    {
      label: 'CA commandes (période)',
      value: `${num(stats.revenue)} ${stats.currency}`,
      sub: pct(stats.deltas.revenue),
    },
  ];
  const boxGap = 5;
  const boxW = (contentW - boxGap) / 2;
  const boxH = 19;
  kpis.forEach((kpi, i) => {
    const col = i % 2;
    if (col === 0) ensureSpace(boxH + boxGap);
    const x = MARGIN + col * (boxW + boxGap);
    doc.setFillColor(...SURFACE);
    doc.rect(x, y, boxW, boxH, 'F');
    doc.setDrawColor(...LINE);
    doc.rect(x, y, boxW, boxH, 'S');
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.text(kpi.label, x + 4, y + 7);
    doc.setTextColor(...INK);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text(kpi.value, x + 4, y + 14.5);
    if (kpi.sub) {
      doc.setFontSize(9);
      doc.setTextColor(kpi.sub.startsWith('-') ? 190 : 150, kpi.sub.startsWith('-') ? 30 : 110, 60);
      doc.text(kpi.sub, x + boxW - 4, y + 8, { align: 'right' });
    }
    if (col === 1) y += boxH + boxGap;
  });
  y += 6;

  // ── Traction ──────────────────────────────────────────────
  sectionTitle('Traction');
  const tractionRows: [string, string][] = [
    ['Croissance 7 jours', `${num(stats.traction.views7d)}  (${pct(stats.traction.growth7d)})`],
    ['Croissance 30 jours', `${num(stats.traction.views30d)}  (${pct(stats.traction.growth30d)})`],
    [
      'Taux de retour visiteurs',
      stats.traction.returningRate != null
        ? `${stats.traction.returningRate.toFixed(0)}%  (${num(stats.traction.returningVisitors)} / ${num(stats.traction.totalVisitors)})`
        : '—',
    ],
  ];
  tractionRows.forEach(([label, value]) => {
    ensureSpace(9);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...SECONDARY);
    doc.text(label, MARGIN, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...INK);
    doc.text(value, pageW - MARGIN, y, { align: 'right' });
    y += 6.5;
    doc.setDrawColor(...LINE);
    doc.line(MARGIN, y - 3, pageW - MARGIN, y - 3);
  });
  y += 6;

  // ── Audience par type ─────────────────────────────────────
  sectionTitle('Audience par type de page');
  const typeRows: [string, number][] = [
    ["Page d'accueil Zifek", stats.landingVisits],
    ['Sites de la plateforme', stats.siteVisits],
    ['Pages produits', stats.productVisits],
    ['Pages services', stats.serviceVisits],
  ];
  const typeMax = Math.max(1, ...typeRows.map(([, v]) => v));
  typeRows.forEach(([label, value]) => {
    ensureSpace(10);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...SECONDARY);
    doc.text(label, MARGIN, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...INK);
    doc.text(num(value), pageW - MARGIN, y, { align: 'right' });
    const barW = contentW - 40;
    doc.setFillColor(...LINE);
    doc.rect(MARGIN + 40, y + 1.2, barW, 2.2, 'F');
    doc.setFillColor(...ACCENT);
    doc.rect(MARGIN + 40, y + 1.2, (barW * value) / typeMax, 2.2, 'F');
    y += 8;
  });
  y += 4;

  // ── Contenus & publications ───────────────────────────────
  sectionTitle('Contenus & publications');
  const contentRows: [string, number][] = [
    ['Annonces marketplace', stats.marketplace.annoncesTotal],
    ['Produits marketplace', stats.marketplace.annoncesProducts],
    ['Services marketplace', stats.marketplace.annoncesServices],
    ['Annonces publiées sur la période', stats.marketplace.annoncesPublished],
    ['Produits boutique', stats.marketplace.productItems],
    ['Produits numériques', stats.marketplace.digitalProducts],
    ['Services des sites', stats.marketplace.servicesItems],
    ['Sites de la plateforme', stats.sites],
    ['Comptes utilisateurs', stats.siteUsers],
  ];
  const contentGap = 4;
  const cBoxW = (contentW - contentGap * 2) / 3;
  contentRows.forEach(([label, value], i) => {
    const col = i % 3;
    if (col === 0) ensureSpace(15 + contentGap);
    const x = MARGIN + col * (cBoxW + contentGap);
    doc.setFillColor(...SURFACE);
    doc.rect(x, y, cBoxW, 14, 'F');
    doc.setTextColor(...INK);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(num(value), x + 3, y + 6.5);
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    const wrapped = doc.splitTextToSize(label, cBoxW - 6);
    doc.text(wrapped[0] || label, x + 3, y + 11.5);
    if (col === 2 || i === contentRows.length - 1) y += 14 + contentGap;
  });
  y += 4;

  // ── Géographie ────────────────────────────────────────────
  // ── Acquisition (nouveaux sites / comptes par mois) ───────
  sectionTitle('Acquisition — nouveaux sites & comptes');
  const acq = stats.acquisition || [];
  if (acq.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text("Aucune donnée d'acquisition disponible.", MARGIN, y);
    y += 8;
  } else {
    const chartH = 32;
    ensureSpace(chartH + 16);
    const maxAcq = Math.max(1, ...acq.map((a) => Math.max(a.sites, a.accounts, a.sitesLastYear)));
    const stepX = contentW / acq.length;
    // Légende : sites (année en cours), sites (année précédente), nouveaux comptes.
    doc.setFillColor(...ACCENT);
    doc.rect(MARGIN, y - 2.2, 2.6, 2.6, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...SECONDARY);
    doc.text('Sites (cette année)', MARGIN + 4, y);
    doc.setFillColor(...SECONDARY);
    doc.rect(MARGIN + 40, y - 2.2, 2.6, 2.6, 'F');
    doc.text('Sites (an dernier)', MARGIN + 44, y);
    doc.setFillColor(...ZIFEK_TERRACOTTA);
    doc.rect(MARGIN + 76, y - 2.2, 2.6, 2.6, 'F');
    doc.text('Comptes', MARGIN + 80, y);
    const chartTop = y + 4;
    const baseY = chartTop + chartH;
    acq.forEach((a, i) => {
      const x = MARGIN + i * stepX;
      const barW = Math.max(1.6, stepX * 0.2);
      const x0 = x + stepX * 0.18;
      const h1 = (a.sites / maxAcq) * chartH;
      const h2 = (a.sitesLastYear / maxAcq) * chartH;
      const h3 = (a.accounts / maxAcq) * chartH;
      doc.setFillColor(...ACCENT);
      doc.rect(x0, baseY - h1, barW, h1, 'F');
      doc.setFillColor(...SECONDARY);
      doc.rect(x0 + barW + 1, baseY - h2, barW, h2, 'F');
      doc.setFillColor(...ZIFEK_TERRACOTTA);
      doc.rect(x0 + (barW + 1) * 2, baseY - h3, barW, h3, 'F');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(...MUTED);
      doc.text(a.month, x + stepX / 2, baseY + 4, { align: 'center' });
    });
    doc.setDrawColor(...LINE);
    doc.line(MARGIN, baseY, pageW - MARGIN, baseY);
    y = baseY + 12;
  }
  y += 4;

  sectionTitle('Répartition géographique');
  if (stats.geoCountries.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text('Aucune donnée géographique sur cette période.', MARGIN, y);
    y += 8;
  } else {
    doc.setFontSize(10);
    stats.geoCountries.slice(0, 10).forEach((country) => {
      ensureSpace(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...SECONDARY);
      doc.text(`${country.name}${country.code ? ` (${country.code})` : ''}`, MARGIN, y);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...INK);
      doc.text(num(country.value), pageW - MARGIN, y, { align: 'right' });
      y += 6;
    });
  }
  y += 4;

  // ── Classements ───────────────────────────────────────────
  const ranking = (
    title: string,
    items: { title: string; value: number }[],
  ) => {
    sectionTitle(title);
    if (items.length === 0) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(...MUTED);
      doc.text('Aucune donnée sur cette période.', MARGIN, y);
      y += 8;
      return;
    }
    doc.setFontSize(10);
    items.slice(0, 8).forEach((item, index) => {
      ensureSpace(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...SECONDARY);
      const label = doc.splitTextToSize(`${index + 1}. ${item.title}`, contentW - 30)[0];
      doc.text(label, MARGIN, y);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...INK);
      doc.text(num(item.value), pageW - MARGIN, y, { align: 'right' });
      y += 6.5;
    });
    y += 4;
  };

  ranking('Sites les plus visités', stats.topSites);
  ranking('Produits les plus visités', stats.topProducts);
  ranking('Services les plus visités', stats.topServices);

  // ── Pied de page ──────────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p += 1) {
    doc.setPage(p);
    doc.setDrawColor(...LINE);
    doc.line(MARGIN, pageH - 12, pageW - MARGIN, pageH - 12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text('Zifek — Rapport investisseurs', MARGIN, pageH - 7);
    doc.text(`Page ${p} / ${totalPages}`, pageW - MARGIN, pageH - 7, { align: 'right' });
  }

  doc.save(`zifek-rapport-investisseurs_${meta.startDate}_${meta.endDate}.pdf`);
}